import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTheme } from "styled-components";
import Loading from "../../UI/Loading";
import { createSearchParams, useNavigate } from "react-router-dom";
import { createQuote } from "../../../requests/quoteManagementRequests";
import { PageWrapper } from "../../UI/Grid";
import QuoteTable from "./QuoteTable";
import Table from "../../UI/Table";
import guid from "../../../modules/guid";
import ConfiguratorContext from "../../../context/configurator/ConfiguratorContext";
import UserContext from "../../../context/user/UserContext";
import { useQuery } from "../../Hooks";
import { getProducts } from "../../../requests/productManagementRequests";
import PagerContextProvider from "../../UI/Pager/PagerContext";
import * as constants from "../../../modules/constants";

const Quotes = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setQuote, setActiveProduct, setIsReconfigure } =
    useContext(ConfiguratorContext);
  const { activeUser, setQueries, queries, pid } = useContext(UserContext);
  const { setConfiguratorMode } = useContext(ConfiguratorContext);

  const _quickConfig = useQuery({
    queryID: "quickConfig",
    request: getProducts,
    forcedFilters: {},
    skip: !pid || !activeUser,
    queryVariables: {
      isQuickConfig: true,
      isNonConfigurable: false,
      pid,
      gid: activeUser?.groupId,
    },
    attributes: ["ID", "img", "base64Image", "ImageUrl"],
  });
  const { data, isLoading } = _quickConfig || {};

  const { rows: products = [] } = data || {};

  const [isCreating, setIsCreating] = useState(false);

  const productGridData = useMemo(() => {
    if (products && products?.length > 0)
      return products.map(({ ID, img, base64Image, ImageUrl }) => ({
        id: ID,
        image: img || base64Image || ImageUrl,
      }));
    return null;
  }, [products]);

  const onRowClick = useCallback(
    (row, e) => {
      e.stopPropagation();
      setQuote({ ...row?.original });
      navigate(`/Quote/${row.original.id}`);
    },
    [navigate, setQuote]
  );

  const onProductClick = useCallback(
    async (product) => {
      setIsReconfigure(false);
      const { id: productId } = product || {};
      const _queries = { ...queries };
      delete _queries?.quotes;
      setQueries(_queries);
      setActiveProduct(productId);
      setConfiguratorMode(constants.CONFIGURATOR_MODES.Default);
      const newId = guid.GenerateGUID();

      const { id: quoteId } = await createQuote({
        input: {
          username: activeUser?.username,
          createdByFirstName: activeUser?.firstName,
          createdByLastName: activeUser?.lastName,
          type: activeUser?.roleLevel === 0 ? 3 : 1,
          _id: newId,
          pid,
          createdByUserId: activeUser?.id,
          groupId: activeUser?.groupId,
        },
      });

      navigate({
        pathname: `/Configurator/${productId}/${quoteId}/${quoteId}_1`,
        search: createSearchParams({
          lineNumber: 1,
        }).toString(),
      });
    },
    [
      activeUser,
      navigate,
      pid,
      queries,
      setActiveProduct,
      setConfiguratorMode,
      setIsReconfigure,
      setQueries,
    ]
  );

  const onCreateClick = useCallback(async () => {
    setIsCreating(true);
    const newId = guid.GenerateGUID();
    const { id: newQuoteId } = await createQuote({
      input: {
        username: activeUser?.username,
        createdByFirstName: activeUser?.firstName,
        createdByLastName: activeUser?.lastName,
        type: activeUser?.roleLevel === 0 ? 3 : 1,
        _id: newId,
        pid,
        createdByUserId: activeUser?.id,
        groupId: activeUser?.groupId,
      },
    });
    const _queries = { ...queries };
    delete _queries?.quotes;
    setQueries(_queries);

    setIsCreating(false);

    navigate(`/Quote/${newQuoteId}`);
  }, [activeUser, navigate, queries, setQueries, pid]);

  const createButtonText = useMemo(() => {
    return isCreating ? (
      <Loading inline color1={theme.secondary} color2={theme.success} />
    ) : (
      "New Quote"
    );
  }, [isCreating, theme.secondary, theme.success]);

  return (
    <PageWrapper>
      {productGridData?.length > 0 && (
        <Table
          hideBack
          title="Quick Configure"
          createButtonText={createButtonText}
          createButtonIcon="faFileCirclePlus"
          createLink={onCreateClick}
          gridData={productGridData}
          hidePager
          gridSize="small"
          forceGrid
          hideProperties
          hideSearch
          hideToggle
          onCardClick={onProductClick}
          isLoading={productGridData?.length < 1 && isLoading}
          relativeLoading
        />
      )}
      {activeUser?.roleLevel === 0 ? null : (
        <PagerContextProvider defaultOrder={["createdDate", "DESC"]}>
          <QuoteTable
            quickConfigLoading={productGridData?.length < 1 && isLoading}
            onRowClick={onRowClick}
            createButtonIcon="faFileCirclePlus"
            createButtonText={createButtonText}
            createLink={onCreateClick}
            hideCreate={productGridData?.length > 0}
          />
        </PagerContextProvider>
      )}

      {/* <PagerContextProvider defaultOrder={["createdDate", "DESC"]}>
        <QuoteTable
          quickConfigLoading={productGridData?.length < 1 && isLoading}
          isQuoteRequest
          hideCreate
          onRowClick={onRowClick}
        />
      </PagerContextProvider> */}
    </PageWrapper>
  );
};

export default Quotes;
